import { ConflictErrorCode } from '@today/types'
import { type InvitationFirstLoginLabels, InvitationFirstLoginView } from '@today/ui-domain'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'
import { useAuthLogo } from '@/auth/useAuthLogo'
import { useCompleteFirstLogin } from '@/hooks/useCompleteFirstLogin'
import { AUTH_NAMESPACE } from '@/i18n'

/** This step never shows the password form, so there is nothing to submit. */
const noPasswordStep = () => undefined

/**
 * The refusals this leg explains in words; any other code reads as an invalid
 * link (TO-728 Copy table — `LOGIN_METHOD_MISMATCH` deliberately keeps that fallback).
 */
const EXPLAINED_REFUSALS = [
	ConflictErrorCode.INVITATION_EXPIRED,
	ConflictErrorCode.INVITATION_REVOKED,
	ConflictErrorCode.INVITATION_SUPERSEDED,
	ConflictErrorCode.INVITATION_ALREADY_USED,
	ConflictErrorCode.FEDERATED_IDENTITY_CONFLICT,
	ConflictErrorCode.FEDERATED_LOGIN_REQUIRED,
]

/** The return leg's copy in the active language (TO-728); the view itself stays i18n-free. */
function useReturnLegLabels(): InvitationFirstLoginLabels {
	const { t } = useTranslation(AUTH_NAMESPACE)
	return {
		title: (slug) => t('invitation.title', { slug }),
		checking: t('invitation.checking'),
		redirecting: t('invitation.redirecting'),
		invalidLink: t('invitation.invalidLink'),
		refusals: Object.fromEntries(EXPLAINED_REFUSALS.map((code) => [code, t(`invitation.refusals.${code}`)])),
	}
}

/** `/t/:slug/invitation/complete` — the federated first login's return leg (TO-309). */
export function InvitationCompletePage() {
	const { slug = '' } = useParams()
	const logo = useAuthLogo()
	const labels = useReturnLegLabels()
	const { stage, errorCode } = useCompleteFirstLogin(slug)

	return (
		<InvitationFirstLoginView
			logo={logo}
			tenantSlug={slug}
			stage={stage}
			errorCode={errorCode}
			labels={labels}
			onSubmitPassword={noPasswordStep}
		/>
	)
}
